import React from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import { DialogueOption } from '../../types';

interface DialogueOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectOption: (option: DialogueOption) => void;
  originalText: string;
  action: string;
  options: DialogueOption[];
  isLoading?: boolean;
  followingTurnsCount?: number;
}

const DialogueOptionsModal: React.FC<DialogueOptionsModalProps> = ({
  isOpen,
  onClose,
  onSelectOption,
  originalText,
  action,
  options,
  isLoading = false,
  followingTurnsCount = 0,
}) => {
  const isMultiTurn = followingTurnsCount > 0;
  const getActionTitle = (action: string) => {
    switch (action) {
      case 'shorten':
        return 'Shorten Options';
      case 'elaborate':
        return 'Elaborate Options';
      case 'rephrase':
        return 'Rephrase Options';
      case 'regenerate':
        return 'Regenerate Options';
      default:
        return 'Modification Options';
    }
  };

  const getActionDescription = (action: string) => {
    switch (action) {
      case 'shorten':
        return 'Choose how to make this dialogue more concise while keeping the essential message.';
      case 'elaborate':
        return 'Choose how to add more detail and emotion to this dialogue.';
      case 'rephrase':
        return 'Choose how to rephrase this dialogue for better clarity and empathy.';
      case 'regenerate':
        return 'Choose a completely different approach to this dialogue.';
      default:
        return 'Choose your preferred modification option.';
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-6">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    {getActionTitle(action)}
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="rounded-md p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="mb-6">
                  <p className="text-sm text-gray-600 mb-4">
                    {getActionDescription(action)}
                  </p>
                  
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      {isMultiTurn ? `Regenerating ${followingTurnsCount + 1} Turn${followingTurnsCount + 1 > 1 ? 's' : ''}` : 'Original Text:'}
                    </h4>
                    {isMultiTurn ? (
                      <p className="text-sm text-blue-600">
                        This will replace the selected turn and {followingTurnsCount} following turn{followingTurnsCount > 1 ? 's' : ''}.
                      </p>
                    ) : (
                      <p className="text-sm text-gray-900 italic">"{originalText}"</p>
                    )}
                  </div>
                </div>

                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-gray-600">Generating options...</span>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {options && options.map((option, index) => (
                      <div
                        key={option.id}
                        className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer group"
                        onClick={() => onSelectOption(option)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center mb-2">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-3">
                                Option {index + 1}
                              </span>
                              {option.description && (
                                <span className="text-sm text-gray-600">{option.description}</span>
                              )}
                            </div>
                            
                            {isMultiTurn && option.turns && option.turns.length > 0 ? (
                              <div className="space-y-3 mb-3">
                                {option.turns.map((turn, idx) => (
                                  <div key={idx} className="border-l-4 border-blue-300 pl-4 py-2 bg-gray-50 rounded-r">
                                    <div className="text-xs font-semibold text-gray-600 mb-1">
                                      Turn {turn.turnNumber}: {turn.speaker}
                                    </div>
                                    <p className="text-sm text-gray-900 leading-relaxed">
                                      "{turn.line}"
                                    </p>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-gray-900 mb-3 leading-relaxed">
                                "{option.text}"
                              </p>
                            )}
                            
                            {option.reasoning && (
                              <div className="bg-blue-50 rounded-md p-3">
                                <h5 className="text-xs font-medium text-blue-800 mb-1">Why this works:</h5>
                                <p className="text-xs text-blue-700">{option.reasoning}</p>
                              </div>
                            )}
                          </div>
                          
                          <div className="ml-4 flex-shrink-0">
                            <div className="w-8 h-8 rounded-full border-2 border-gray-300 group-hover:border-blue-500 flex items-center justify-center transition-colors">
                              <CheckIcon className="w-4 h-4 text-transparent group-hover:text-blue-500 transition-colors" />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DialogueOptionsModal;
